import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import plusIcon from '../../assets/img/icons/arrow_drop_down.svg';
import minusIcon from '../../assets/img/icons/arrow_drop_up.svg';
import errorIcon from '../../assets/img/icons/errorIcon.svg';
import '../../assets/css/componentStyles/NavGroup.css';

function NavGroup({
  title,
  hasError,
  children,
  defaultOpen,
  className,
  disabled,
  onToggle,
  ariaLabel,
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const toggle = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (disabled) return;

      const newState = !isOpen;
      setIsOpen(newState);

      if (onToggle) {
        onToggle(newState);
      }
    },
    [isOpen, disabled, onToggle],
  );

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault(); // Prevent scrolling when spacebar is pressed
        toggle(e);
      }
    },
    [toggle],
  );

  const containerClassName = classNames('nav-group-container', className, {
    'nav-group-container--open': isOpen,
    'nav-group-container--error': hasError,
    'nav-group-container--disabled': disabled,
  });

  const toggleClassName = classNames('nav-group-toggle', {
    'nav-group-toggle--disabled': disabled,
    'nav-group-toggle--error': hasError,
  });

  return (
    <div className={containerClassName}>
      <div
        className={toggleClassName}
        onClick={toggle}
        onKeyDown={handleKeyPress}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isOpen}
        aria-controls={`nav-group-content-${title.replace(/\s+/g, '-').toLowerCase()}`}
        aria-label={ariaLabel || `Toggle ${title} section`}
        aria-disabled={disabled}
      >
        <span className="nav-group-title">{title}</span>
        <div className="nav-group-controls ml-auto">
          {hasError && <img src={errorIcon} alt="error" className="ml-auto" height={20} />}
          <img
            src={isOpen ? minusIcon : plusIcon}
            alt=""
            className="nav-group-toggle-icon"
            style={{
              transition: 'transform 0.2s ease-in-out',
            }}
            aria-hidden="true"
          />
        </div>
      </div>

      <div
        id={`nav-group-content-${title.replace(/\s+/g, '-').toLowerCase()}`}
        className={classNames('nav-group-content', {
          'nav-group-content--open': isOpen,
        })}
        aria-hidden={!isOpen}
      >
        {isOpen && (
          <ul className="nav nav-group-children">
            {React.Children.map(children, (child) => (
              <li key={child.key || child.props?.id || Math.random()} className="nav-item">
                {child}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

NavGroup.propTypes = {
  title: PropTypes.string.isRequired,
  hasError: PropTypes.bool,
  children: PropTypes.node.isRequired,
  defaultOpen: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onToggle: PropTypes.func,
  ariaLabel: PropTypes.string,
};

NavGroup.defaultProps = {
  hasError: false,
  defaultOpen: true,
  className: '',
  disabled: false,
  onToggle: null,
  ariaLabel: null,
};

export default NavGroup;
